<template>
  <div class="latest-routes">
    <div class="section-header">
      <h3>
        <el-icon><Timer /></el-icon>
        最新生成的路线
      </h3>
      <el-button 
        type="text" 
        @click="refreshRoutes"
        :loading="loading"
      >
        <el-icon><Refresh /></el-icon>
        刷新
      </el-button>
    </div>

    <el-empty v-if="!loading && routes.length === 0" description="暂无路线数据" />

    <el-row :gutter="20" v-loading="loading">
      <el-col 
        v-for="route in routes" 
        :key="route.id"
        :xs="24"
        :sm="12"
        :md="8"
        :lg="8"
        class="route-col"
      >
        <el-card 
          class="latest-route-card"
          shadow="hover"
          @click="viewRoute(route)"
        >
          <div class="route-image-small" :style="{ backgroundImage: `url(${getRouteImage(route)})` }">
            <div class="image-overlay">
              <el-tag :type="getThemeType(route.theme)" size="small" effect="dark">
                {{ route.theme }}
              </el-tag>
            </div>
          </div>

          <div class="route-info">
            <h4 class="route-name">{{ route.name }}</h4>
            
            <div class="route-meta">
              <span>
                <el-icon><MapLocation /></el-icon>
                {{ route.totalDistance }}km
              </span>
              <span>
                <el-icon><Money /></el-icon>
                ¥{{ route.budgetDetails?.total?.toLocaleString() }}
              </span>
              <span>
                <el-icon><StarFilled /></el-icon>
                {{ route.rating }}
              </span>
            </div>

            <div class="route-date">
              <el-icon><Calendar /></el-icon>
              {{ formatDate(route.createdAt) }}
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 查看详情弹窗 -->
    <MapModal 
      v-model="modalVisible"
      :route="selectedRoute"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { Timer, Refresh, MapLocation, Money, StarFilled, Calendar } from '@element-plus/icons-vue'
import { getLatestRoutes } from '../api'
import { ElMessage } from 'element-plus'
import MapModal from './MapModal.vue'

const routes = ref([])
const loading = ref(false)
const modalVisible = ref(false)
const selectedRoute = ref({})

const getRouteImage = (route) => {
  const themeImages = {
    '亲子': 'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=400',
    '情侣': 'https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=400',
    '摄影': 'https://images.unsplash.com/photo-1452421822248-d4c2b47f0c81?w=400',
    '美食': 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400',
    '山水': 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400'
  }
  return themeImages[route.theme] || themeImages['山水']
}

const getThemeType = (theme) => {
  const types = {
    '亲子': 'success',
    '情侣': 'danger',
    '摄影': 'primary',
    '美食': 'warning',
    '山水': 'info'
  }
  return types[theme] || 'info'
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('zh-CN', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const refreshRoutes = async () => {
  loading.value = true
  try {
    const response = await getLatestRoutes()
    if (response.success) {
      routes.value = response.routes
    } else {
      routes.value = []
    }
  } catch (error) {
    console.error('Failed to load latest routes:', error)
    ElMessage.error('加载最新路线失败')
    routes.value = []
  } finally {
    loading.value = false
  }
}

const viewRoute = (route) => {
  selectedRoute.value = route
  modalVisible.value = true
}

onMounted(() => {
  refreshRoutes()
})
</script>

<style scoped>
.latest-routes {
  margin-top: 40px;
  padding: 20px 0;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-header h3 {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 20px;
  color: #333;
}

.section-header h3 .el-icon {
  color: #00B5FF;
  font-size: 24px;
}

.route-col {
  margin-bottom: 20px;
}

.latest-route-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 12px;
  overflow: hidden;
}

.latest-route-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.route-image-small {
  height: 150px;
  background-size: cover;
  background-position: center;
  position: relative;
  margin: -20px -20px 15px -20px;
}

.image-overlay {
  position: absolute;
  top: 10px;
  right: 10px;
}

.route-info {
  padding: 5px;
}

.route-name {
  margin: 0 0 10px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.route-meta {
  display: flex;
  gap: 15px;
  margin-bottom: 10px;
  font-size: 13px;
  color: #666;
}

.route-meta span {
  display: flex;
  align-items: center;
  gap: 4px;
}

.route-meta .el-icon {
  font-size: 14px;
  color: #00B5FF;
}

.route-date {
  font-size: 12px;
  color: #999;
  display: flex;
  align-items: center;
  gap: 4px;
}

.route-date .el-icon {
  font-size: 12px;
}
</style>